import { useLayoutEffect, useMemo, type ReactNode } from "react";
import clsx from "clsx";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useVirtualizerInitialMeasure } from "./useVirtualScrollElement";

/** Below this row count a plain list is cheaper than virtualiser setup. */
const DEFAULT_VIRTUALIZE_THRESHOLD = 24;
/** Default bottom gap between rows, in pixels. */
const DEFAULT_GAP_PX = 8;
const DEFAULT_OVERSCAN = 6;

/**
 * Props for {@link VirtualList}.
 *
 * @typeParam T - The item type rendered by each row.
 */
export interface VirtualListProps<T> {
  /** Ordered items to render. */
  items: T[];
  /** Stable unique key for an item (used for React keys and virtual item keys). */
  getKey: (item: T, index: number) => string;
  /** Renders the content of a single row. Spacing is handled by the list — do not add row margins. */
  renderItem: (item: T, index: number) => ReactNode;
  /**
   * Initial row height in pixels before the row is measured. A rough estimate
   * is fine: real heights are measured from the DOM, so spacing stays correct
   * even when this is wrong.
   */
  estimateSize: (item: T, index: number) => number;
  /**
   * The scroll parent element. Must be state-backed so the virtualiser rebinds
   * after the scrollport mounts (see {@link useVirtualScrollElement}).
   */
  scrollElement: HTMLElement | null;
  /** True once the scrollport has a non-zero height. Defaults to ``true``. */
  scrollReady?: boolean;
  /** Vertical gap between rows, in pixels. Applied identically in plain and virtual modes. */
  gap?: number;
  /** Switch from plain list to virtualiser at/above this count. Defaults to 24. */
  threshold?: number;
  /** Extra rows rendered above/below the viewport. Defaults to 6. */
  overscan?: number;
  /**
   * Identity of the visible slice (bucket / filter). Changing it scrolls the
   * list back to the top.
   */
  listKey?: string;
  /** When set, scroll this item key into view (used before deep-link row flash). */
  focusItemKey?: string | null;
  /** Class applied to the list container. */
  className?: string;
  /** Test id applied to the list container. */
  "data-testid"?: string;
  /** Accessible name for the list. */
  ariaLabel?: string;
}

/**
 * Virtualised list with guaranteed-correct vertical spacing.
 *
 * Rows are measured from the DOM (``measureElement``), so spacing is correct
 * regardless of each row's height — callers never tune pixel estimates against
 * the row component. Below ``threshold`` it renders a plain flex list; the same
 * ``gap`` is used in both modes so switching is seamless. During the first
 * virtual measure pass it overlays the plain list to avoid a layout flash.
 *
 * Do not add vertical margins/padding to row content for spacing — pass ``gap``
 * instead, otherwise virtual and plain modes diverge.
 *
 * @example
 * const { ref, element, scrollReady } = useVirtualScrollElement<HTMLDivElement>();
 * return (
 *   <div ref={ref} className="flex-1 overflow-y-auto">
 *     <VirtualList
 *       items={rows}
 *       getKey={(r) => r.id}
 *       estimateSize={() => 92}
 *       renderItem={(r) => <Row row={r} />}
 *       scrollElement={element}
 *       scrollReady={scrollReady}
 *     />
 *   </div>
 * );
 */
export function VirtualList<T>({
  items,
  getKey,
  renderItem,
  estimateSize,
  scrollElement,
  scrollReady = true,
  gap = DEFAULT_GAP_PX,
  threshold = DEFAULT_VIRTUALIZE_THRESHOLD,
  overscan = DEFAULT_OVERSCAN,
  listKey,
  focusItemKey,
  className,
  "data-testid": testId,
  ariaLabel,
}: VirtualListProps<T>) {
  const wantsVirtualize = items.length >= threshold;
  const canVirtualize = wantsVirtualize && scrollElement !== null && scrollReady;

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElement,
    estimateSize: (index) => estimateSize(items[index], index),
    gap,
    overscan,
    enabled: canVirtualize,
    getItemKey: (index) => getKey(items[index], index),
    useAnimationFrameWithResizeObserver: true,
  });

  // Recompute layout when the visible slice changes (keys are a cheap revision signal).
  const itemsRevision = useMemo(
    () => items.map((item, index) => getKey(item, index)).join("\0"),
    [items, getKey],
  );

  const virtualSized = useVirtualizerInitialMeasure(canVirtualize, itemsRevision);

  // Reset scroll position when the bucket/filter changes.
  useLayoutEffect(() => {
    if (listKey === undefined || !scrollElement) return;
    scrollElement.scrollTop = 0;
    if (canVirtualize) virtualizer.scrollToOffset(0);
  }, [listKey, scrollElement, canVirtualize, virtualizer]);

  // Bring deep-link target rows into the virtual viewport before focus flash runs.
  useLayoutEffect(() => {
    if (!focusItemKey || !scrollElement || !scrollReady) return;
    if (canVirtualize && !virtualSized) return;
    const index = items.findIndex((item, i) => getKey(item, i) === focusItemKey);
    if (index < 0) return;

    const scrollToFocusedRow = () => {
      if (!canVirtualize) return;
      if (!virtualizer.options.enabled) return;
      if (!virtualizer.scrollElement) return;
      try {
        virtualizer.scrollToIndex(index, { align: "center" });
      } catch {
        // TanStack can throw if the scrollport unmounts mid-scroll.
      }
    };

    if (canVirtualize) {
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToFocusedRow);
      });
    }
  }, [focusItemKey, listKey, canVirtualize, scrollElement, scrollReady, virtualSized, virtualizer, items, getKey]);

  const virtualItems = virtualizer.getVirtualItems();
  const showPlainOnly = !canVirtualize;
  const showPlainOverlay = canVirtualize && !virtualSized;

  if (showPlainOnly) {
    return (
      <div
        role="list"
        aria-label={ariaLabel}
        data-testid={testId}
        className={clsx("flex flex-col", className)}
        style={{ rowGap: gap }}
      >
        {items.map((item, index) => (
          <div role="listitem" key={getKey(item, index)}>
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      role="list"
      aria-label={ariaLabel}
      data-testid={testId}
      className={clsx("relative", className)}
    >
      {/* Plain overlay shown until the first measure pass completes, to avoid a height flash. */}
      {showPlainOverlay ? (
        <div
          role="presentation"
          aria-hidden="true"
          className="relative z-[1] flex flex-col pointer-events-none"
          style={{ rowGap: gap }}
        >
          {items.map((item, index) => (
            <div key={`overlay-${getKey(item, index)}`}>{renderItem(item, index)}</div>
          ))}
        </div>
      ) : null}
      <div
        className={clsx(
          "relative w-full",
          showPlainOverlay && "pointer-events-none absolute inset-x-0 top-0 opacity-0",
        )}
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualItems.map((virtualItem) => {
          const index = virtualItem.index;
          const item = items[index];
          return (
            <div
              role="listitem"
              key={virtualItem.key}
              data-index={index}
              ref={virtualizer.measureElement}
              className="absolute left-0 top-0 w-full"
              style={{ transform: `translateY(${virtualItem.start}px)` }}
            >
              {renderItem(item, index)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
